import { inject, provider } from "react-ioc";
import { observer } from "mobx-react";
import React, { Component } from "react";
import { Container, ContainerDetails } from "app/modules/common/layout/components/containers";
import { PageTitleDetails } from "app/modules/common/layout/components/page-title";
import { Loading } from "app/modules/common/layout/components/loading";
import { Chip, Grid, MenuItem, Stack, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { Section, SectionName, SectionSeparator } from "app/modules/common/layout/components/details";
import { LoadingButton } from "@mui/lab";
import { MainStore } from "app/modules/company/stores/MainStore";
import { NavigateHook } from "app/modules/common/layout/hooks/navigate-hook";
import { Paths } from "app/modules/company/stores/paths";
import { ActivateDeactivateSection } from "app/modules/common/details/activate-section";
import { SectionSelectField, SectionTextField } from "app/modules/common/details/text-field";
import { CompanyIntegrationTypeMap } from "app/modules/company/components/company-integrations";
import { CompanyIntegrationDetailsStore } from "app/modules/company/components/company-integrations/details/CompanyIntegrationDetailsStore";
import { CompanyIntegrationType, EmailSmtpSettingType } from "app/modules/company/api/clients.api";
import IntegrationSettings from "./integration-settings";
import { Button } from "@mui/material";

@provider(CompanyIntegrationDetailsStore)
@observer
class Index extends Component<any, any> {
    @inject companyIntegrationDetailsStore: CompanyIntegrationDetailsStore;
    @inject mainStore: MainStore;

    render() {
        if (this.companyIntegrationDetailsStore.isInitLoading) {
            return <Loading />;
        }

        if (this.companyIntegrationDetailsStore.isSavedSuccessfully) {
            return <NavigateHook path={Paths.CompanyIntegrations.nav} />;
        }

        if (this.companyIntegrationDetailsStore.isRedirectToNewEntity) {
            return <NavigateHook path={Paths.EditIntegration.nav(this.companyIntegrationDetailsStore.integrationId)} />;
        }

        return <Container>
            <PageTitleDetails Title={this.companyIntegrationDetailsStore.title} Size={!this.companyIntegrationDetailsStore.isReadOnlyMode ? 8 : 12} />
            {!this.companyIntegrationDetailsStore.isReadOnlyMode && <Grid item xs={4} sx={{ pb: 4 }} textAlign={"right"}>
                <LoadingButton loading={this.companyIntegrationDetailsStore.isLoading} disabled={this.companyIntegrationDetailsStore.formState.hasError} variant="contained" onClick={this.companyIntegrationDetailsStore.save}>
                    Save
                </LoadingButton>
            </Grid>}
            <ContainerDetails>
                <SectionName title="Info" description="Integration Basic Info" />
                <Section>
                    <Stack spacing={2} direction={"column"}>
                        <Stack spacing={2} direction={"row"}>
                            <SectionTextField
                                label={"Name"}
                                isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                field={this.companyIntegrationDetailsStore.formState.$.name}
                                fullWidth
                            />
                            <SectionSelectField
                                field={this.companyIntegrationDetailsStore.formState.$.type}
                                isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode || this.companyIntegrationDetailsStore.integrationId > 0}
                                label={"Type"}
                                none={false}
                                width={"100%"}
                                items={() => Array.from(CompanyIntegrationTypeMap.entries()).map((x) => <MenuItem value={x[0]} key={x[0]}>{x[1]}</MenuItem>)}
                            />
                        </Stack>
                    </Stack>
                </Section>
                <SectionSeparator />
                <SectionName title={this.companyIntegrationDetailsStore.formState.$.type.value === CompanyIntegrationType.VerifiedSmtpSender ? "Verified domains" : "Credentials"} description={this.companyIntegrationDetailsStore.formState.$.type.value === CompanyIntegrationType.VerifiedSmtpSender ? "Tenant verified domains (read-only). From Email on bid settings must use one of these domains." : "Credentials to have an access to the API"} />
                <Section>
                    <Stack spacing={2} direction={"column"}>
                        {this.companyIntegrationDetailsStore.formState.$.type.value === CompanyIntegrationType.VerifiedSmtpSender &&
                            <Stack spacing={1} direction="column">
                                <Typography variant="body2" color="text.secondary">
                                    The following domains are configured for this tenant (Admin → Tenant details). Bid emails sent via this integration must use a From address with one of these domains.
                                </Typography>
                                {this.companyIntegrationDetailsStore.tenantVerifiedDomains && this.companyIntegrationDetailsStore.tenantVerifiedDomains.length > 0 ? (
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                        {this.companyIntegrationDetailsStore.tenantVerifiedDomains.map((d) => (
                                            <Chip key={d} label={d} size="small" variant="outlined" />
                                        ))}
                                    </Stack>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No verified domains. Add domains in Admin → Tenant details to use Verified SMTP Sender.
                                    </Typography>
                                )}
                            </Stack>}
                        {this.companyIntegrationDetailsStore.formState.$.type.value == CompanyIntegrationType.SMTP &&
                            <Stack spacing={2} direction={"row"}>
                                <SectionTextField
                                    label={"Address"}
                                    isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                    field={this.companyIntegrationDetailsStore.formSmtp.$.address}
                                    fullWidth
                                />
                                <SectionTextField
                                    label={"PORT"}
                                    isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                    field={this.companyIntegrationDetailsStore.formSmtp.$.port}
                                    fullWidth
                                />
                                <SectionTextField
                                    label={"User"}
                                    isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                    field={this.companyIntegrationDetailsStore.formSmtp.$.user}
                                    fullWidth
                                />
                                <SectionTextField
                                    label={"Password"}
                                    isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                    field={this.companyIntegrationDetailsStore.formSmtp.$.password}
                                    type={"password"}
                                    fullWidth
                                />
                            </Stack>}
                        {this.companyIntegrationDetailsStore.formState.$.type.value == CompanyIntegrationType.Sylectus &&
                            <Stack spacing={2} direction={"row"}>
                                <SectionTextField
                                    label={"Corporation Id"}
                                    isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                    field={this.companyIntegrationDetailsStore.formSylectus.$.corporationId}
                                    fullWidth
                                />
                                <SectionTextField
                                    label={"Corporation Password"}
                                    isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                    field={this.companyIntegrationDetailsStore.formSylectus.$.corporationPassword}
                                    type={"password"}
                                    fullWidth
                                />
                                <SectionTextField
                                    label={"User Id"}
                                    isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                    field={this.companyIntegrationDetailsStore.formSylectus.$.userId}
                                    fullWidth
                                />
                                <SectionTextField
                                    label={"User Password"}
                                    isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                    field={this.companyIntegrationDetailsStore.formSylectus.$.userPassword}
                                    type={"password"}
                                    fullWidth
                                />
                            </Stack>}
                        {this.companyIntegrationDetailsStore.formState.$.type.value == CompanyIntegrationType.DAT &&
                            <Stack spacing={2} direction={"row"}>
                                <SectionTextField
                                    label={"Corporation Email"}
                                    isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                    field={this.companyIntegrationDetailsStore.formDAT.$.corporationEmail}
                                    fullWidth
                                />
                                <SectionTextField
                                    label={"Corporation Password"}
                                    isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                    field={this.companyIntegrationDetailsStore.formDAT.$.corporationPassword}
                                    type={"password"}
                                    fullWidth
                                />
                            </Stack>}
                        {this.companyIntegrationDetailsStore.formState.$.type.value == CompanyIntegrationType.Inbox &&
                            <React.Fragment>
                                <Stack spacing={2} direction={"row"}>
                                    <SectionTextField
                                        label={"Full Name"}
                                        isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                        field={this.companyIntegrationDetailsStore.formInbox.$.fullName}
                                        fullWidth
                                    />
                                    <SectionTextField
                                        label={"Email"}
                                        isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                        field={this.companyIntegrationDetailsStore.formInbox.$.email}
                                        fullWidth
                                    />
                                    <SectionTextField
                                        label={"Password"}
                                        isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                        field={this.companyIntegrationDetailsStore.formInbox.$.password}
                                        type={"password"}
                                        fullWidth
                                    />
                                    <SectionSelectField
                                        field={this.companyIntegrationDetailsStore.formInbox.$.type}
                                        isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                        label={"Type"}
                                        none={false}
                                        width={"100%"}
                                        items={() => Object.entries(EmailSmtpSettingType)
                                            .filter(([key]) => isNaN(Number(key)))
                                            .map(([key, value]) =>
                                                <MenuItem value={value} key={value}>{key}</MenuItem>
                                            )}
                                    />
                                </Stack>
                                <Stack spacing={2} direction={"column"}>
                                    <Typography variant="body2" color="text.secondary">
                                        <b>Set a filter on your original email address to forward it to the integration:</b>
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <b>To:</b> {this.companyIntegrationDetailsStore.formInbox.$.forwardEmail.value}
                                    </Typography>
                                </Stack>
                            </React.Fragment>}
                        {this.companyIntegrationDetailsStore.formState.$.type.value == CompanyIntegrationType.QBO &&
                            <Stack spacing={2} direction={"column"}>
                                <Stack spacing={2} direction={"row"}>
                                    <SectionSelectField
                                        field={this.companyIntegrationDetailsStore.formQBO.$.environmentType}
                                        isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                        label={"Environment Type"}
                                        none={false}
                                        width={"100%"}
                                        items={() => [
                                            <MenuItem value={0} key={0}>Not set</MenuItem>,
                                            <MenuItem value={1} key={1}>Production</MenuItem>,
                                            <MenuItem value={2} key={2}>Sandbox</MenuItem>
                                        ]}
                                    />
                                    {this.companyIntegrationDetailsStore.integrationId > 0 && (
                                        <>
                                            <SectionSelectField
                                                field={this.companyIntegrationDetailsStore.formQBO.$.billsAccountId}
                                                isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                                label={"Bills Account"}
                                                none={true}
                                                width={"100%"}
                                                items={() => this.companyIntegrationDetailsStore.qboAccounts.map((a) => <MenuItem value={a.id} key={a.id}>{a.name}</MenuItem>)}
                                            />
                                            <SectionSelectField
                                                field={this.companyIntegrationDetailsStore.formQBO.$.invoicesAccountId}
                                                isReadOnlyMode={this.companyIntegrationDetailsStore.isReadOnlyMode}
                                                label={"Invoices Account"}
                                                none={true}
                                                width={"100%"}
                                                items={() => this.companyIntegrationDetailsStore.qboAccounts.map((a) => <MenuItem value={a.id} key={a.id}>{a.name}</MenuItem>)}
                                            />
                                        </>
                                    )}
                                    {this.companyIntegrationDetailsStore.formState.$.type.value == CompanyIntegrationType.QBO && this.companyIntegrationDetailsStore.integrationId > 0 && this.companyIntegrationDetailsStore.qboAccountsError && (
                                        <Typography variant="body2" color="error">
                                            {this.companyIntegrationDetailsStore.qboAccountsError}
                                        </Typography>
                                    )}
                                </Stack>
                                {this.companyIntegrationDetailsStore.integrationId > 0 && (
                                    <Stack spacing={1} direction="column">
                                        {this.companyIntegrationDetailsStore.hasQboRefreshToken ? (
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Chip label="Connected" color="success" size="small" />
                                                <Typography variant="body2" color="text.secondary">
                                                    QuickBooks is authorized. Use Reconnect to re-authorize or Refresh Token to renew access.
                                                </Typography>
                                            </Stack>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Re-authorization required. Use Connect to QuickBooks to authorize.
                                            </Typography>
                                        )}
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <Button
                                                component="a"
                                                href={this.companyIntegrationDetailsStore.getAuthorizeQBOUrl()}
                                                variant={this.companyIntegrationDetailsStore.hasQboRefreshToken ? "text" : "outlined"}
                                                size="small"
                                            >
                                                {this.companyIntegrationDetailsStore.hasQboRefreshToken ? "Reconnect" : "Connect to QuickBooks"}
                                            </Button>
                                            <LoadingButton
                                                loading={this.companyIntegrationDetailsStore.isLoading}
                                                disabled={!this.companyIntegrationDetailsStore.hasQboRefreshToken || !this.companyIntegrationDetailsStore.isActive}
                                                variant="outlined"
                                                size="small"
                                                onClick={() => this.companyIntegrationDetailsStore.refreshQBOToken()}
                                            >
                                                Refresh Token
                                            </LoadingButton>
                                            {this.companyIntegrationDetailsStore.hasQboRefreshToken && (
                                                <LoadingButton
                                                    loading={this.companyIntegrationDetailsStore.isLoadingQboAccounts}
                                                    variant="text"
                                                    size="small"
                                                    onClick={() => this.companyIntegrationDetailsStore.loadQboAccounts()}
                                                >
                                                    Reload accounts
                                                </LoadingButton>
                                            )}
                                        </Stack>
                                    </Stack>
                                )}
                            </Stack>}
                    </Stack>
                </Section>
                {!this.companyIntegrationDetailsStore.isReadOnlyMode && this.companyIntegrationDetailsStore.integrationId > 0 && <ActivateDeactivateSection isActive={this.companyIntegrationDetailsStore.isActive} isLoading={this.companyIntegrationDetailsStore.isLoading} activate={this.companyIntegrationDetailsStore.activate} deactivate={this.companyIntegrationDetailsStore.deactivate} />}

                {this.companyIntegrationDetailsStore.formState.$.type.value === CompanyIntegrationType.Inbox && this.companyIntegrationDetailsStore.integrationId > 0 && (
                    <React.Fragment>
                        <SectionSeparator />
                        <IntegrationSettings integrationId={this.companyIntegrationDetailsStore.integrationId} />
                    </React.Fragment>
                )}
            </ContainerDetails>
        </Container>;
    }

    componentDidMount() {
        const { id } = this.props.urlParams;
        this.companyIntegrationDetailsStore.init(id);
    }
}

export default (props) => {
    const urlParams = useParams();
    return <Index {...props} urlParams={urlParams} key={urlParams.id} />;
}
